// src/tareas/KanbanUI.jsx
import React from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Box, Paper, Typography, Chip } from "@mui/material";

/**
 * KanbanUI (robusto)
 * ------------------------------------------------------------
 * ✅ Layout CRÍTICO en sx (no depende del CssBaseline/theme)
 * ✅ 4 columnas en desktop, 2 en tablet, 1 en móvil
 * ✅ Soporta cualquier nº de columnas (si algún día añades más)
 *
 * Props:
 * - columns: [{ id, title }]
 * - itemsByColumn: { [columnId]: items[] }
 * - getItemId: (item) => string|number
 * - renderCard: (item) => ReactNode
 * - onDragEnd: (result) => void|Promise<void>
 */
export const KanbanUI = ({
  columns,
  itemsByColumn,
  getItemId,
  renderCard,
  onDragEnd,
}) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box
        sx={{
          display: "grid",
          gap: 2,

          // 1 col móvil, 2 tablet, 4 desktop (lo que tú esperas)
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },

          alignItems: "start",
          width: "100%",
        }}
      >
        {columns.map((col) => {
          const items = itemsByColumn?.[col.id] || [];

          return (
            <Box key={col.id} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  backgroundColor: "#f6f7fb",
                }}
              >
                {/* Header columna */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    pb: 1,
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                    {col.title}
                  </Typography>

                  <Chip
                    label={items.length}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: "rgba(0,0,0,0.06)",
                    }}
                  />
                </Box>

                {/* Body (droppable) */}
                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        pt: 0.5,
                        minHeight: 120,
                        borderRadius: 2,

                        outline: snapshot.isDraggingOver
                          ? "2px dashed rgba(65, 182, 230, 0.55)"
                          : "none",
                        outlineOffset: 6,
                      }}
                    >
                      {items.map((item, index) => {
                        const id = String(getItemId(item));

                        return (
                          <Draggable key={id} draggableId={id} index={index}>
                            {(providedDraggable, snapshotDraggable) => (
                              <Box
                                ref={providedDraggable.innerRef}
                                {...providedDraggable.draggableProps}
                                {...providedDraggable.dragHandleProps}
                                sx={{
                                  width: "100%",
                                  boxSizing: "border-box",
                                  backgroundColor: "#fff",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  borderRadius: 2,
                                  p: 1.5,
                                  boxShadow: snapshotDraggable.isDragging
                                    ? "0 10px 24px rgba(0,0,0,0.14)"
                                    : "0 6px 16px rgba(0,0,0,0.06)",
                                }}
                              >
                                {renderCard(item)}
                              </Box>
                            )}
                          </Draggable>
                        );
                      })}

                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            </Box>
          );
        })}
      </Box>
    </DragDropContext>
  );
};

KanbanUI.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  itemsByColumn: PropTypes.object.isRequired,
  getItemId: PropTypes.func.isRequired,
  renderCard: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
};

export default KanbanUI;
